/**
 * Content System Data Access
 *
 * Browser data helpers for Content System brand profiles and outputs.
 *
 * Primary responsibilities:
 * - Load workspace-scoped content system records
 * - Save brand profiles, campaigns, outputs, and execution docs
 * - Normalize database rows into Content System domain records
 *
 * Dependencies:
 * - Supabase browser client
 * - Content System record utilities
 */

import { Json } from '@/supabase/types'
import { supabase } from '@/lib/supabase/browser-client'
import {
  BrandGuidelineIteration,
  BrandProfile,
  ContentActivityEntry,
  ContentAssetPack,
  ContentCampaign,
  ContentExecutionDoc,
  ContentOutputRecord
} from '@/lib/content-system/types'
import {
  createEmptyAssetPack,
  createEmptyBrandProfile,
  createEmptyCampaign,
  createEmptyOutputRecord,
  createExecutionDocRecord,
  normalizeBrandColorTokens,
  normalizeBrandLogoGuidelines,
  normalizeBrandSocialProduction,
  normalizeBrandTypographySettings,
  normalizeContentBlogPack,
  normalizeContentVisualPack
} from '@/lib/content-system/utils'

type ContentSystemQueryClient = {
  from: (table: string) => any
}

interface BrandProfileRow {
  id: string
  created_at: string
  updated_at: string
  workspace_id: string
  user_id: string | null
  name: string
  company_name: string
  audience: string
  offer: string
  brand_summary: string
  voice: string
  pillars: string[]
  dos: string[]
  donts: string[]
  banned_words: string[]
  preferred_phrases: string[]
  visual_direction: string
  brand_colors: Json | null
  typography: Json | null
  logo_guidelines: Json | null
  social_production: Json | null
  website_urls: string[]
  notes: string
  guideline_markdown: string
  guideline_iterations: Json | null
  active_guideline_iteration_id: string | null
  brand_guideline_file_ids: string[]
  inspiration_file_ids: string[]
  brand_logo_file_ids: string[]
  social_template_file_ids: string[]
  status: BrandProfile['status']
  archived_at: string | null
  last_used_at: string | null
  activity_log: Json | null
}

interface CampaignRow {
  id: string
  created_at: string
  updated_at: string
  workspace_id: string
  user_id: string | null
  name: string
  topic: string
  primary_objective: string
  audience: string
  offer: string
  key_message: string
  call_to_action: string
  additional_context: string
  campaign_weeks: number
  posts_per_week: number
  platforms: string[]
  brand_profile_id: string | null
  reference_file_ids: string[]
  status: ContentCampaign['status']
  outputs: Json | null
  output_record_ids: string[]
  last_generated_output_id: string | null
  review_notes: string
  activity_log: Json | null
}

interface OutputRecordRow {
  id: string
  created_at: string
  updated_at: string
  workspace_id: string
  user_id: string | null
  name: string
  campaign_id: string | null
  brand_profile_id: string | null
  source_campaign_name: string
  topic: string
  status: ContentOutputRecord['status']
  model: string
  asset_pack: Json | null
  execution_doc_ids: string[]
  generated_asset_file_ids: string[]
  wordpress_draft_id: number | null
  wordpress_draft_url: string | null
  activity_log: Json | null
}

interface ExecutionDocRow {
  id: string
  created_at: string
  updated_at: string
  workspace_id: string
  user_id: string | null
  name: string
  doc_type: ContentExecutionDoc['docType']
  campaign_id: string | null
  output_record_id: string | null
  brand_profile_id: string | null
  platform: ContentExecutionDoc['platform']
  content: string
}

function getContentSystemClient() {
  return supabase as unknown as ContentSystemQueryClient
}

function normalizeStringArray(value: unknown) {
  if (!Array.isArray(value)) {
    return []
  }

  return value.map((item) => (typeof item === 'string' ? item : '')).filter(Boolean)
}

function getGeneratedAssetMetadataFileIds(assetPack: ContentAssetPack) {
  return assetPack.visuals.generatedAssets.map((asset) => asset.fileId).filter(Boolean)
}

function mergeGeneratedAssetFileIds(fileIds: unknown, assetPack: ContentAssetPack) {
  return Array.from(
    new Set([
      ...normalizeStringArray(fileIds),
      ...getGeneratedAssetMetadataFileIds(assetPack)
    ])
  )
}

function normalizeActivityLog(value: Json | null) {
  if (!Array.isArray(value)) {
    return []
  }

  return value as ContentActivityEntry[]
}

function normalizeGuidelineIterations(value: Json | null) {
  if (!Array.isArray(value)) {
    return []
  }

  return value as BrandGuidelineIteration[]
}

function normalizeAssetPack(value: Json | null, fallbackModel: string) {
  const emptyAssetPack = createEmptyAssetPack()
  const parsed =
    value && typeof value === 'object' && !Array.isArray(value)
      ? (value as Partial<ContentAssetPack>)
      : {}

  return {
    ...emptyAssetPack,
    ...parsed,
    strategy: {
      ...emptyAssetPack.strategy,
      ...(parsed.strategy || {})
    },
    calendar: Array.isArray(parsed.calendar) ? parsed.calendar : [],
    blog: normalizeContentBlogPack(parsed.blog),
    social: {
      ...emptyAssetPack.social,
      ...(parsed.social || {})
    },
    visuals: normalizeContentVisualPack(parsed.visuals, parsed.social || {}),
    repurposing: Array.isArray(parsed.repurposing) ? parsed.repurposing : [],
    qualityChecklist: Array.isArray(parsed.qualityChecklist) ? parsed.qualityChecklist : [],
    generatedAt:
      typeof parsed.generatedAt === 'string' && parsed.generatedAt
        ? parsed.generatedAt
        : emptyAssetPack.generatedAt,
    model: typeof parsed.model === 'string' && parsed.model ? parsed.model : fallbackModel
  } satisfies ContentAssetPack
}

function mapBrandProfileRow(row: BrandProfileRow): BrandProfile {
  return {
    ...createEmptyBrandProfile(),
    id: row.id,
    kind: 'brand-profile',
    name: row.name || '',
    companyName: row.company_name || 'Marshmallow Studio',
    audience: row.audience || '',
    offer: row.offer || '',
    brandSummary: row.brand_summary || '',
    voice: row.voice || '',
    pillars: normalizeStringArray(row.pillars),
    dos: normalizeStringArray(row.dos),
    donts: normalizeStringArray(row.donts),
    bannedWords: normalizeStringArray(row.banned_words),
    preferredPhrases: normalizeStringArray(row.preferred_phrases),
    visualDirection: row.visual_direction || '',
    brandColors: normalizeBrandColorTokens(row.brand_colors),
    typography: normalizeBrandTypographySettings(row.typography),
    logoGuidelines: normalizeBrandLogoGuidelines(row.logo_guidelines),
    socialProduction: normalizeBrandSocialProduction(row.social_production),
    websiteUrls: normalizeStringArray(row.website_urls),
    notes: row.notes || '',
    guidelineMarkdown: row.guideline_markdown || '',
    guidelineIterations: normalizeGuidelineIterations(row.guideline_iterations),
    activeGuidelineIterationId: row.active_guideline_iteration_id,
    brandGuidelineFileIds: normalizeStringArray(row.brand_guideline_file_ids),
    inspirationFileIds: normalizeStringArray(row.inspiration_file_ids),
    brandLogoFileIds: normalizeStringArray(row.brand_logo_file_ids),
    socialTemplateFileIds: normalizeStringArray(row.social_template_file_ids),
    status: row.status || 'active',
    archivedAt: row.archived_at,
    lastUsedAt: row.last_used_at,
    activityLog: normalizeActivityLog(row.activity_log),
    createdAt: row.created_at,
    updatedAt: row.updated_at
  }
}

function mapCampaignRow(row: CampaignRow): ContentCampaign {
  return {
    ...createEmptyCampaign(),
    id: row.id,
    kind: 'campaign',
    name: row.name || '',
    topic: row.topic || '',
    primaryObjective: row.primary_objective || 'Drive awareness and qualified demand',
    audience: row.audience || '',
    offer: row.offer || '',
    keyMessage: row.key_message || '',
    callToAction: row.call_to_action || '',
    additionalContext: row.additional_context || '',
    campaignWeeks: row.campaign_weeks || 4,
    postsPerWeek: row.posts_per_week || 4,
    platforms: normalizeStringArray(row.platforms) as ContentCampaign['platforms'],
    brandProfileId: row.brand_profile_id,
    referenceFileIds: normalizeStringArray(row.reference_file_ids),
    status: row.status || 'draft',
    outputs:
      row.outputs && typeof row.outputs === 'object' && !Array.isArray(row.outputs)
        ? normalizeAssetPack(
            row.outputs,
            typeof (row.outputs as { model?: unknown }).model === 'string'
              ? (row.outputs as { model: string }).model || 'losi-omega'
              : 'losi-omega'
          )
        : null,
    outputRecordIds: normalizeStringArray(row.output_record_ids),
    lastGeneratedOutputId: row.last_generated_output_id,
    reviewNotes: row.review_notes || '',
    activityLog: normalizeActivityLog(row.activity_log),
    createdAt: row.created_at,
    updatedAt: row.updated_at
  }
}

function mapOutputRecordRow(row: OutputRecordRow): ContentOutputRecord {
  const assetPack = normalizeAssetPack(row.asset_pack, row.model || 'losi-omega')

  return {
    ...createEmptyOutputRecord(),
    id: row.id,
    kind: 'asset-pack',
    name: row.name || '',
    campaignId: row.campaign_id,
    brandProfileId: row.brand_profile_id,
    sourceCampaignName: row.source_campaign_name || '',
    topic: row.topic || '',
    status: row.status || 'generated',
    model: row.model || 'losi-omega',
    assetPack,
    executionDocIds: normalizeStringArray(row.execution_doc_ids),
    generatedAssetFileIds: mergeGeneratedAssetFileIds(row.generated_asset_file_ids, assetPack),
    wordpressDraftId: typeof row.wordpress_draft_id === 'number' ? row.wordpress_draft_id : null,
    wordpressDraftUrl: row.wordpress_draft_url,
    activityLog: normalizeActivityLog(row.activity_log),
    createdAt: row.created_at,
    updatedAt: row.updated_at
  }
}

function mapExecutionDocRow(row: ExecutionDocRow): ContentExecutionDoc {
  return {
    ...createExecutionDocRecord(),
    id: row.id,
    kind: 'execution-doc',
    name: row.name || '',
    docType: row.doc_type,
    campaignId: row.campaign_id,
    outputRecordId: row.output_record_id,
    brandProfileId: row.brand_profile_id,
    platform: row.platform,
    content: row.content || '',
    createdAt: row.created_at,
    updatedAt: row.updated_at
  }
}

function mapBrandProfileToRow(record: BrandProfile, workspaceId: string, userId?: string) {
  return {
    workspace_id: workspaceId,
    ...(userId ? { user_id: userId } : {}),
    name: record.name,
    company_name: record.companyName,
    audience: record.audience,
    offer: record.offer,
    brand_summary: record.brandSummary,
    voice: record.voice,
    pillars: record.pillars,
    dos: record.dos,
    donts: record.donts,
    banned_words: record.bannedWords,
    preferred_phrases: record.preferredPhrases,
    visual_direction: record.visualDirection,
    brand_colors: record.brandColors,
    typography: record.typography,
    logo_guidelines: record.logoGuidelines,
    social_production: record.socialProduction,
    website_urls: record.websiteUrls,
    notes: record.notes,
    guideline_markdown: record.guidelineMarkdown,
    guideline_iterations: record.guidelineIterations,
    active_guideline_iteration_id: record.activeGuidelineIterationId,
    brand_guideline_file_ids: record.brandGuidelineFileIds,
    inspiration_file_ids: record.inspirationFileIds,
    brand_logo_file_ids: record.brandLogoFileIds,
    social_template_file_ids: record.socialTemplateFileIds,
    status: record.status,
    archived_at: record.archivedAt,
    last_used_at: record.lastUsedAt,
    activity_log: record.activityLog,
    created_at: record.createdAt,
    updated_at: record.updatedAt
  }
}

function mapCampaignToRow(record: ContentCampaign, workspaceId: string, userId?: string) {
  return {
    workspace_id: workspaceId,
    ...(userId ? { user_id: userId } : {}),
    name: record.name,
    topic: record.topic,
    primary_objective: record.primaryObjective,
    audience: record.audience,
    offer: record.offer,
    key_message: record.keyMessage,
    call_to_action: record.callToAction,
    additional_context: record.additionalContext,
    campaign_weeks: record.campaignWeeks,
    posts_per_week: record.postsPerWeek,
    platforms: record.platforms,
    brand_profile_id: record.brandProfileId,
    reference_file_ids: record.referenceFileIds,
    status: record.status,
    outputs: record.outputs,
    output_record_ids: record.outputRecordIds,
    last_generated_output_id: record.lastGeneratedOutputId,
    review_notes: record.reviewNotes,
    activity_log: record.activityLog,
    created_at: record.createdAt,
    updated_at: record.updatedAt
  }
}

function mapOutputRecordToRow(record: ContentOutputRecord, workspaceId: string, userId?: string) {
  const generatedAssetFileIds = mergeGeneratedAssetFileIds(
    record.generatedAssetFileIds,
    record.assetPack
  )

  return {
    workspace_id: workspaceId,
    ...(userId ? { user_id: userId } : {}),
    name: record.name,
    campaign_id: record.campaignId,
    brand_profile_id: record.brandProfileId,
    source_campaign_name: record.sourceCampaignName,
    topic: record.topic,
    status: record.status,
    model: record.model,
    asset_pack: record.assetPack,
    execution_doc_ids: record.executionDocIds,
    generated_asset_file_ids: generatedAssetFileIds,
    wordpress_draft_id: record.wordpressDraftId,
    wordpress_draft_url: record.wordpressDraftUrl,
    activity_log: record.activityLog,
    created_at: record.createdAt,
    updated_at: record.updatedAt
  }
}

function mapExecutionDocToRow(record: ContentExecutionDoc, workspaceId: string, userId?: string) {
  return {
    workspace_id: workspaceId,
    ...(userId ? { user_id: userId } : {}),
    name: record.name,
    doc_type: record.docType,
    campaign_id: record.campaignId,
    output_record_id: record.outputRecordId,
    brand_profile_id: record.brandProfileId,
    platform: record.platform,
    content: record.content,
    created_at: record.createdAt,
    updated_at: record.updatedAt
  }
}

export async function getContentSystemWorkspaceData(workspaceId: string) {
  const client = getContentSystemClient()

  const [brandsResult, campaignsResult, outputsResult, executionDocsResult] = await Promise.all([
    client
      .from('content_system_brand_profiles')
      .select('*')
      .eq('workspace_id', workspaceId)
      .order('updated_at', { ascending: false }),
    client
      .from('content_system_campaigns')
      .select('*')
      .eq('workspace_id', workspaceId)
      .order('updated_at', { ascending: false }),
    client
      .from('content_system_output_records')
      .select('*')
      .eq('workspace_id', workspaceId)
      .order('updated_at', { ascending: false }),
    client
      .from('content_system_execution_docs')
      .select('*')
      .eq('workspace_id', workspaceId)
      .order('updated_at', { ascending: false })
  ])

  const error =
    brandsResult.error || campaignsResult.error || outputsResult.error || executionDocsResult.error

  if (error) {
    throw new Error(error.message || 'Failed to load content system records')
  }

  return {
    brandProfiles: ((brandsResult.data || []) as BrandProfileRow[]).map(mapBrandProfileRow),
    campaigns: ((campaignsResult.data || []) as CampaignRow[]).map(mapCampaignRow),
    outputRecords: ((outputsResult.data || []) as OutputRecordRow[]).map(mapOutputRecordRow),
    executionDocs: ((executionDocsResult.data || []) as ExecutionDocRow[]).map(mapExecutionDocRow)
  }
}

export async function getContentSystemBrandProfiles(workspaceId: string) {
  const { data, error } = await getContentSystemClient()
    .from('content_system_brand_profiles')
    .select('*')
    .eq('workspace_id', workspaceId)
    .order('updated_at', { ascending: false })

  if (error) {
    throw new Error(error.message || 'Failed to load brand profiles')
  }

  return ((data || []) as BrandProfileRow[]).map(mapBrandProfileRow)
}

export async function saveContentSystemBrandProfile(
  record: BrandProfile,
  workspaceId: string,
  userId: string
) {
  const client = getContentSystemClient()

  if (record.id) {
    const { data, error } = await client
      .from('content_system_brand_profiles')
      .update(mapBrandProfileToRow(record, workspaceId))
      .eq('id', record.id)
      .select('*')
      .single()

    if (error || !data) {
      throw new Error(error?.message || 'Failed to save brand profile')
    }

    return mapBrandProfileRow(data as BrandProfileRow)
  }

  const { data, error } = await client
    .from('content_system_brand_profiles')
    .insert([mapBrandProfileToRow(record, workspaceId, userId)])
    .select('*')
    .single()

  if (error || !data) {
    throw new Error(error?.message || 'Failed to create brand profile')
  }

  return mapBrandProfileRow(data as BrandProfileRow)
}

export async function saveContentSystemCampaign(
  record: ContentCampaign,
  workspaceId: string,
  userId: string
) {
  const client = getContentSystemClient()

  if (record.id) {
    const { data, error } = await client
      .from('content_system_campaigns')
      .update(mapCampaignToRow(record, workspaceId))
      .eq('id', record.id)
      .select('*')
      .single()

    if (error || !data) {
      throw new Error(error?.message || 'Failed to save campaign')
    }

    return mapCampaignRow(data as CampaignRow)
  }

  const { data, error } = await client
    .from('content_system_campaigns')
    .insert([mapCampaignToRow(record, workspaceId, userId)])
    .select('*')
    .single()

  if (error || !data) {
    throw new Error(error?.message || 'Failed to create campaign')
  }

  return mapCampaignRow(data as CampaignRow)
}

export async function saveContentSystemOutputRecord(
  record: ContentOutputRecord,
  workspaceId: string,
  userId: string
) {
  const client = getContentSystemClient()

  if (record.id) {
    const { data, error } = await client
      .from('content_system_output_records')
      .update(mapOutputRecordToRow(record, workspaceId))
      .eq('id', record.id)
      .select('*')
      .single()

    if (error || !data) {
      throw new Error(error?.message || 'Failed to save output record')
    }

    return mapOutputRecordRow(data as OutputRecordRow)
  }

  const { data, error } = await client
    .from('content_system_output_records')
    .insert([mapOutputRecordToRow(record, workspaceId, userId)])
    .select('*')
    .single()

  if (error || !data) {
    throw new Error(error?.message || 'Failed to create output record')
  }

  return mapOutputRecordRow(data as OutputRecordRow)
}

export async function saveContentSystemExecutionDoc(
  record: ContentExecutionDoc,
  workspaceId: string,
  userId: string
) {
  const client = getContentSystemClient()

  if (record.id) {
    const { data, error } = await client
      .from('content_system_execution_docs')
      .update(mapExecutionDocToRow(record, workspaceId))
      .eq('id', record.id)
      .select('*')
      .single()

    if (error || !data) {
      throw new Error(error?.message || 'Failed to save execution doc')
    }

    return mapExecutionDocRow(data as ExecutionDocRow)
  }

  const { data, error } = await client
    .from('content_system_execution_docs')
    .insert([mapExecutionDocToRow(record, workspaceId, userId)])
    .select('*')
    .single()

  if (error || !data) {
    throw new Error(error?.message || 'Failed to create execution doc')
  }

  return mapExecutionDocRow(data as ExecutionDocRow)
}

export async function deleteContentSystemBrandProfile(id: string) {
  const { error } = await getContentSystemClient()
    .from('content_system_brand_profiles')
    .delete()
    .eq('id', id)

  if (error) {
    throw new Error(error.message || 'Failed to delete brand profile')
  }
}

export async function deleteContentSystemCampaign(id: string) {
  const { error } = await getContentSystemClient()
    .from('content_system_campaigns')
    .delete()
    .eq('id', id)

  if (error) {
    throw new Error(error.message || 'Failed to delete campaign')
  }
}

export async function deleteContentSystemOutputRecord(id: string) {
  const { error } = await getContentSystemClient()
    .from('content_system_output_records')
    .delete()
    .eq('id', id)

  if (error) {
    throw new Error(error.message || 'Failed to delete output record')
  }
}

export async function deleteContentSystemExecutionDoc(id: string) {
  const { error } = await getContentSystemClient()
    .from('content_system_execution_docs')
    .delete()
    .eq('id', id)

  if (error) {
    throw new Error(error.message || 'Failed to delete execution doc')
  }
}
